<template>
  <!-- L1 category tab row: top-level categories with horizontal scroll + fade arrows.
       Shared between the markets page (filters the in-page grid) and the product
       details page (navigates back to markets, filtered). Mostly stateless (active
       tab driven by `activeId`, clicks emitted via `select`) except for the
       Conditional category's "NEW" badge, which self-contains its own
       rollout-window state via useConditionalsBadge() (FORECASTUI-2583). -->
  <div class="flex items-center gap-4">
    <div class="relative flex-1 min-w-0">
      <div ref="l1ScrollEl" class="overflow-x-auto scrollbar-none" @scroll="updateL1Scroll">
        <!-- Selection is driven by @click on each Tab (not Tabs' @update:value) so
           re-clicking the already-active tab still emits — the parent uses that
           to scroll back to the top of the page. -->
        <Tabs unstyled :value="activeId ?? ''">
          <TabList
            unstyled
            :pt="{
              tablist: { class: 'flex gap-[24px] min-w-max' },
              content: { class: 'min-w-max' },
            }"
          >
            <Tab
              v-if="includePopular"
              unstyled
              :value="POPULAR_TAB_ID"
              :class="l1TabClass(POPULAR_TAB_ID)"
              @click="emit('select', POPULAR_TAB_ID)"
            >
              {{ $t('popular') }}
            </Tab>
            <Tab
              v-for="cat in items"
              :key="cat.id"
              v-tooltip.bottom="
                isConditionalCategory(cat.id)
                  ? { value: $t('conditionalTooltip'), class: 'question-tooltip' }
                  : undefined
              "
              unstyled
              :value="cat.id"
              :class="l1TabClass(cat.id)"
              @click="selectL1(cat.id)"
            >
              {{ cat.name }}
              <span
                v-if="cat.restricted"
                class="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-semibold text-white bg-black rounded-full leading-tight align-middle"
                >R</span
              >
              <span
                v-if="isConditionalCategory(cat.id) && showConditionalBadge"
                class="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-semibold text-on-accent bg-accent rounded uppercase leading-tight align-middle"
                >{{ $t('conditionalNewBadge') }}</span
              >
            </Tab>
          </TabList>
        </Tabs>
      </div>
      <!-- Left arrow -->
      <button
        v-if="l1CanScrollLeft"
        class="absolute left-0 top-0 bottom-0 w-[60px] flex items-center justify-start pl-[2px] cursor-pointer z-10 scroll-fade-left"
        @click="scrollL1('left')"
      >
        <i class="ibi-chevron-left text-[18px] text-muted-color" />
      </button>
      <!-- Right arrow -->
      <button
        v-if="l1CanScrollRight"
        class="absolute right-0 top-0 bottom-0 w-[60px] flex items-center justify-end pr-[2px] cursor-pointer z-10 scroll-fade-right"
        @click="scrollL1('right')"
      >
        <i class="ibi-chevron-right text-[18px] text-muted-color" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import type { ForecastTraderCategoryWithId } from '@/types/forecasttrader.types';
import { useConditionalsBadge } from '@/composables/useConditionalsBadge';
import { POPULAR_TAB_ID, CONDITIONAL_CATEGORY_ID } from './constants';

const props = withDefaults(
  defineProps<{
    items: ForecastTraderCategoryWithId[];
    activeId: string | null;
    includePopular?: boolean;
  }>(),
  { includePopular: false },
);

const emit = defineEmits<{ select: [id: string] }>();

const { showBadge: showConditionalBadge, markSeen: markConditionalSeen } = useConditionalsBadge();

// Conditional is now a real L1 category from the tree, so it renders through the
// generic `items` loop — including the `R` restricted pill, since it arrives
// restricted like any other internal-only category. It adds two bespoke touches
// on top: the explainer tooltip and the NEW badge, which clears on first visit
// (FORECASTUI-2583).
const isConditionalCategory = (id: string) => id === CONDITIONAL_CATEGORY_ID;

const selectL1 = (id: string) => {
  if (isConditionalCategory(id)) markConditionalSeen();
  emit('select', id);
};

const l1ScrollEl = ref<HTMLElement | null>(null);
const l1CanScrollLeft = ref(false);
const l1CanScrollRight = ref(false);

const updateL1Scroll = () => {
  const el = l1ScrollEl.value;
  if (!el) return;
  l1CanScrollLeft.value = el.scrollLeft > 5;
  l1CanScrollRight.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 5;
};

const scrollL1 = (direction: 'left' | 'right') => {
  l1ScrollEl.value?.scrollBy({ left: direction === 'left' ? -200 : 200, behavior: 'smooth' });
};

// Re-check scroll affordances when the item set changes, on mount, and on resize.
watch(
  () => props.items,
  () => nextTick(updateL1Scroll),
);
onMounted(() => {
  nextTick(updateL1Scroll);
  window.addEventListener('resize', updateL1Scroll);
});
onUnmounted(() => window.removeEventListener('resize', updateL1Scroll));

const l1TabClass = (id: string) => [
  'py-[12px] text-[16px] leading-[20px] whitespace-nowrap border-b-4 transition-colors cursor-pointer',
  props.activeId === id
    ? 'border-accent font-semibold'
    : 'border-transparent text-on-surface-secondary hover:text-color',
];
</script>

<style scoped>
.scrollbar-none {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
}

.scroll-fade-left {
  background: linear-gradient(to right, var(--p-content-background) 30%, transparent 100%);
}

.scroll-fade-right {
  background: linear-gradient(to left, var(--p-content-background) 30%, transparent 100%);
}
</style>
<!-- last modified by AI tools on 2026-10-09 -->
